// 6sense API Integration Service for Lead Enrichment

export interface SixSenseEnrichmentRequest {
  email?: string;
  domain?: string;
  linkedInUrl?: string;
  peopleId?: string;
  referenceKeys?: Record<string, any>;
}

export interface SixSensePersonData {
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  jobTitle: string;
  seniority: string;
  department: string;
  linkedInUrl?: string;
  phone?: string;
  location: {
    city: string;
    state: string;
    country: string;
  };
  company: SixSenseCompanyData;
}

export interface SixSenseCompanyData {
  name: string;
  domain: string;
  industry: string;
  subIndustry: string;
  employeeCount: number;
  employeeRange: string;
  revenue: number;
  revenueRange: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  technographics: string[];
  socialProfiles: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
  };
  founded: number;
  description: string;
}

export interface SixSenseEnrichmentResponse {
  success: boolean;
  person: SixSensePersonData;
  confidence: number;
  credits_consumed: number;
  enriched_fields: string[];
}

export interface SixSenseCompanyEnrichmentResponse {
  success: boolean;
  company: SixSenseCompanyData;
  confidence: number;
  credits_consumed: number;
  enriched_fields: string[];
}

export interface SixSenseConfig {
  apiToken: string;
  baseUrl?: string;
}

class SixSenseApiService {
  private config: SixSenseConfig | null = null;
  private baseUrl = 'https://api.6sense.com';

  constructor() {
    this.initializeConfig();
  }

  private initializeConfig() {
    // Only use localStorage for API token storage - never bundle tokens in client
    // Environment variables with VITE_ prefix get bundled and expose secrets
  }

  public setupTokens(apiToken: string, baseUrl?: string) {
    this.config = {
      apiToken,
      baseUrl: baseUrl || this.baseUrl
    };
  }

  private async makeRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' = 'POST',
    data?: any,
    contentType: string = 'application/json'
  ): Promise<T> {
    if (!this.config?.apiToken) {
      throw new Error('6sense API token not configured. Please call setupTokens() to configure the API token.');
    }

    const url = `${this.config.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Authorization': `Token ${this.config.apiToken}`,
      'Content-Type': contentType
    };

    let body: string | FormData | undefined;

    if (data) {
      if (contentType === 'application/x-www-form-urlencoded') {
        const formData = new URLSearchParams();
        Object.keys(data).forEach(key => {
          if (data[key] !== undefined && data[key] !== null) {
            formData.append(key, data[key]);
          }
        });
        body = formData.toString();
      } else {
        body = JSON.stringify(data);
      }
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`6sense API error: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }

      return await response.json();
    } catch (error) {
      console.error('6sense API request failed:', error);
      throw error;
    }
  }

  async enrichPerson(request: SixSenseEnrichmentRequest): Promise<SixSenseEnrichmentResponse> {
    return this.makeRequest<SixSenseEnrichmentResponse>(
      '/v2/enrichment/people',
      'POST',
      request,
      'application/json'
    );
  }

  async enrichCompany(request: SixSenseEnrichmentRequest): Promise<SixSenseCompanyEnrichmentResponse> {
    return this.makeRequest<SixSenseCompanyEnrichmentResponse>(
      '/v1/enrichment/company',
      'POST',
      request,
      'application/x-www-form-urlencoded'
    );
  }

  async enrichLead(email: string, domain?: string): Promise<{
    person: SixSenseEnrichmentResponse | null;
    company: SixSenseCompanyEnrichmentResponse | null;
  }> {
    try {
      const [personResponse, companyResponse] = await Promise.allSettled([
        this.enrichPerson({ email }),
        this.enrichCompany({ email, domain })
      ]);

      return {
        person: personResponse.status === 'fulfilled' ? personResponse.value : null,
        company: companyResponse.status === 'fulfilled' ? companyResponse.value : null
      };
    } catch (error) {
      console.error('Failed to enrich lead:', error);
      return {
        person: null,
        company: null
      };
    }
  }

  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      if (!this.config?.apiToken) {
        return {
          success: false,
          message: 'API token not configured'
        };
      }

      // Test with a simple request
      await this.enrichPerson({ email: 'test@example.com' });
      return {
        success: true,
        message: 'Connection successful'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Connection failed'
      };
    }
  }
}

// Export singleton instance
export const sixSenseApi = new SixSenseApiService();

// React Query helpers
export const sixSenseQueries = {
  enrichPerson: (request: SixSenseEnrichmentRequest) => ({
    queryKey: ['sixsense', 'person', request],
    queryFn: () => sixSenseApi.enrichPerson(request),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  }),

  enrichCompany: (request: SixSenseEnrichmentRequest) => ({
    queryKey: ['sixsense', 'company', request],
    queryFn: () => sixSenseApi.enrichCompany(request),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  }),

  enrichLead: (email: string, domain?: string) => ({
    queryKey: ['sixsense', 'lead', email, domain],
    queryFn: () => sixSenseApi.enrichLead(email, domain),
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1
  }),

  connection: () => ({
    queryKey: ['sixsense', 'connection'],
    queryFn: () => sixSenseApi.testConnection(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    retry: 2
  })
};

// Utility function to setup tokens
export const setupSixSenseTokens = (apiToken: string, baseUrl?: string) => {
  sixSenseApi.setupTokens(apiToken, baseUrl);
};